import { Attachment, getFilenameWithoutExtension, getExtension, filenamify } from '@jms/model';
import { ConfigService } from '@jms/ui/ui.standalone.services/config.service';;
import { PdfService } from '@workbuddy/services';
import { FileGroup } from '../components/file.list/file.list.component';
import { JustMeta } from 'client-zip';
import { inject } from '@angular/core';

export class FilesZipService {

    protected pdfService = inject(PdfService);

    public downloadGroups(zipName: string, groups: FileGroup[]) {

        const fileNames = new Map<string, number>();
        const files = [];

        for (let group of groups) {

            for (let x of group.attachments) {

                const info = this.processAttachment(x, fileNames, group);

                if (info?.input) {
                    files.push(info);
                    continue;
                }
            }
        }

        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'files';
        input.value = JSON.stringify(files);

        const form = document.createElement('form');
        form.method = 'POST';
        form.action = `/download-zip/${encodeURIComponent(zipName)}`;
        form.appendChild(input);
        document.body.appendChild(form);
        form.submit();
        form.remove();

    }

    public downloadAttachments(zipName: string, attachments: Attachment[]) {

        const fileNames = new Map<string, number>();
        const files: JustMeta[] = [];

        for (let x of attachments) {

            const info = this.processAttachment(x, fileNames, null)

            if (info?.input) {
                files.push(info);
                continue;
            }
        }

        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'files';
        input.value = JSON.stringify(files);

        const form = document.createElement('form');
        form.method = 'POST';
        form.action = `/download-zip/${encodeURIComponent(zipName)}`;
        form.appendChild(input);
        document.body.appendChild(form);
        form.submit();
        form.remove();

    }

    private processAttachment(x: Attachment, fileNames: Map<string, number>, group: FileGroup): JustMeta {

        const environment = ConfigService.environment;
        const filename = filenamify(getFilenameWithoutExtension(x.title), { replacement: '_' });

        let extension = '';

        switch (x.type) {
            case 'Signature':
                extension = 'png';
                break;
            case 'Form':
                extension = 'pdf';
                break;
            default:
                extension = getExtension(x.path);
                break;
        }

        const groupName = group ? filenamify(group.title, { replacement: '_' }) : null;

        let fullName = groupName
            ? `${groupName}/${filename}.${extension}`
            : `${filename}.${extension}`;

        if (fileNames.has(fullName)) {
            const count = fileNames.get(fullName);

            fullName = groupName
                ? `${groupName}/${filename}_${count}.${extension}`
                : `${filename}_${count}.${extension}`;

            fileNames.set(fullName, count + 1);

        } else {
            fileNames.set(fullName, 1);

        }

        const info = {
            name: fullName,
            input: null,
            size: 0
        }

        switch (x.type) {
            case 'File':
            case 'Image':
            case 'Video':
                info.input = x.path;
                break;

            case 'Signature': {
                const image = (!x.path?.startsWith('data:image'))
                    ? `data:image/png;base64,${x.path}`
                    : x.path;
                info.input = image;
                break;
            }

            case 'Form':

                if (x.path?.endsWith('pdf')) {
                    info.input = x.path;
                } else if (x.data) {
                    const formPath = `https://${environment.tenant}.${environment.domain}/forms/${x.data}`;
                    const pdfPath = this.pdfService.generatePdfUrl(formPath) + '&formate=blob';
                    info.input = pdfPath;
                } else if (x.path) {
                    info.input = x.path;
                }
                break;

            default:
                // Metadata rows sometimes omit type or use an unexpected value — still zip by path.
                if (x.path) info.input = x.path;
                break;
        }

        return info;

    }

}